<template>
    <section class="car-not-found" id="carNotFound">
        <div class="car-not-found__container container">
            <StepBar />

            <StepLink :step="1" />

            <h3 class="title">Your details</h3>

            <div class="card car-not-found__card">
                <p class="car-not-found__copy">
                    The registration you entered may be a private registration or is not currently registered. Please enter your vehicle details below and your nearest dealership will get back to you as soon as possible.
                </p>

                <h3 class="car-not-found__sub-heading">Vehicle Details</h3>
                <form @submit.prevent="submit" ref="form" method="post" class="material-form" data-form-name="Online Service Booking - Not my car form">
                    <div class="form-row">
                        <div class="input__container">
                            <label for="registration" class="car-not-found__label">Registration</label>
                            <input
                                type="text"
                                name="registration"
                                id="registration"
                                placeholder="Enter registration"
                                v-model="registration"
                                class="form-control"
                                :class="{ active: this.registration != '' }"
                                data-validate="input"
                                data-validate-message="Please enter vehicle registration"
                            />
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="input__container">
                            <label for="make" class="car-not-found__label" v-show="!otherSelected">Vehicle Make</label>
                            <select
                                name="make"
                                id="make"
                                v-model="make"
                                class="form-control form-control--select"
                                :class="{ active: this.make != '' }"
                                @change="handleMakeChange"
                                data-validate="select"
                                data-validate-message="Please select vehicle make"
                            >
                                <option selected disabled value="">Select make</option>
                                <option value="Citroen">Citroën</option>
                                <option value="Peugeot">Peugeot</option>
                                <option value="DS">DS</option>
                                <option value="Vauxhall">Vauxhall</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div class="input__container" v-show="!otherSelected">
                            <label for="model" class="car-not-found__label">Vehicle model</label>
                            <select
                                name="model"
                                id="model"
                                v-model="model"
                                class="form-control form-control--select"
                                :class="{ active: this.model != '' }"
                                :data-validate="otherSelected ? false : 'select'"
                                data-validate-message="Please select vehicle model"
                            >
                                <option selected disabled value="">Select model</option>
                                <option v-for="(model, index) in modelList" :value="model" :key="index">{{ model }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-row" v-show="otherSelected">
                        <div class="input__container">
                            <label for="otherMake" class="car-not-found__label">Vehicle make</label>
                            <input
                                type="text"
                                name="otherMake"
                                id="otherMake"
                                placeholder="Enter make of vehicle"
                                v-model="nonFranchiseVehicle.make"
                                class="form-control"
                                :class="{ active: this.nonFranchiseVehicle.make != '' }"
                                :data-validate="otherSelected ? 'input' : false"
                                data-validate-message="Please enter vehicle make"
                            />
                        </div>
                        <div class="input__container">
                            <label for="otherModel" class="car-not-found__label">Vehicle model</label>
                            <input
                                type="text"
                                name="otherModel"
                                id="otherModel"
                                placeholder="Enter model of vehicle"
                                v-model="nonFranchiseVehicle.model"
                                class="form-control"
                                :class="{ active: this.nonFranchiseVehicle.model != '' }"
                                :data-validate="otherSelected ? 'input' : false"
                                data-validate-message="Please enter vehicle model"
                            />
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="input__container">
                            <label for="yearOfManufacture" class="car-not-found__label">Year of manufacture</label>
                            <input
                                type="text"
                                name="yearOfManufacture"
                                id="yearOfManufacture"
                                placeholder="Year of manufacture"
                                v-model="yearOfManufacture"
                                class="form-control"
                                :class="{ active: this.yearOfManufacture != '' }"
                                data-validate="input"
                                data-validate-message="Please enter vehicle year of manufacture"
                            />
                        </div>

                        <div class="input__container">
                            <label for="fuelType" class="car-not-found__label">Fuel type</label>
                            <select
                                name="fuelType"
                                id="fuelType"
                                v-model="fuelType"
                                class="form-control form-control--select"
                                :class="{ active: this.fuelType != '' }"
                                data-validate="select"
                                data-validate-message="Please select vehicle fuel type"
                            >
                                <option selected disabled value="">Select fuel type</option>
                                <option value="Petrol">Petrol</option>
                                <option value="Diesel">Diesel</option>
                                <option value="Electric">Electric</option>
                                <option value="Hybrid">Hybrid</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-continue__container">
                        <button type="submit" class="btn btn--primary btn--full-width form-continue__button">Continue</button>
                    </div>
                </form>
            </div>
        </div>
    </section>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import { StepBar, StepLink } from ".";

const badgeIds = {
    Citroen: 21,
    Peugeot: 20,
    DS: 23,
    Vauxhall: 22,
    other: false,
};

const fuelIds = {
    Petrol: 70,
    Diesel: 71,
    Electric: 73,
};

const yearConversion = {
    0: "200",
    5: "200",
    1: "201",
    6: "201",
    2: "202",
    7: "202",
    3: "203",
    8: "203",
};

export default {
    components: {
        StepBar,
        StepLink,
    },

    data() {
        return {
            badgeId: "",
            fuelTypeId: "",
            modelList: [],
            validator: false,
            make: "",
            model: "",
            fuelType: "",
            yearOfManufacture: "",
        };
    },

    computed: {
        ...mapState("OSB", ["otherSelected", "nonFranchiseVehicle", "registration", "selectedVehicle", "bookingId"]),

        registration: {
            get: function () {
                return this.$store.state.OSB.registration;
            },
            set: function (newValue) {
                this.setRegistration(newValue);
                // this.setSelectedVehicle({
                //     ...this.selectedVehicle,
                //     registration: newValue.toUpperCase(),
                //     year_of_manufacture: this.bestGuessAgeFromReg(),
                // });
            },
        },

        isValidUKRegistration() {
            const regex =
                /(^[A-Za-z]{2}[0-9]{2}\s{0,1}[A-Za-z]{3}$)|(^[A-Za-z][0-9]{1,3}\s{0,1}[A-Za-z]{3}$)|(^[A-Za-z]{3} [0-9]{1,3}[A-Za-z]$)|(^[0-9]{1,4}\s{0,1}[A-Za-z]{1,2}$)|(^[0-9]{1,3}\s{0,1}[A-Za-z]{1,3}$)|(^[A-Za-z]{1,2}\s{0,1}[0-9]{1,4}$)|(^[A-Za-z]{1,3}\s{0,1}[0-9]{1,3}$)/;
            return regex.test(this.registration);
        },
    },

    watch: {
        make(val) {
            val === "other" ? this.setOtherSelected(true) : this.setOtherSelected(false);
        },
        fuelType(val) {
            this.fuelTypeId = fuelIds[val] || "";
        },

        otherSelected(val) {
            document.querySelectorAll("div.validation-error").forEach((error) => error.remove());

            this.$nextTick(() => {
                this.validator.updateValidatableElements();
            });
        },
    },

    methods: {
        ...mapMutations("OSB", ["incrementStep", "setSelectedVehicle", "setBrand", "setRegistration", "setPricingData", "setOtherSelected", "setStateForNotMyVehicle"]),
        ...mapActions("OSB", ["collectServicePlan"]),

        async submit() {
            if (!await this.validator.isValid()) {
                return;
            }

            this.validator.preventInput();

            let make = this.make;
            let model = this.model;
            if (this.make == 'other') {
                make = this.nonFranchiseVehicle.make;
                model = this.nonFranchiseVehicle.model;
            }

            this.setSelectedVehicle({
                bookingId: this.bookingId,
                make: make,
                model: model,
                fuelType: this.fuelType,
                yearOfManufacture: this.yearOfManufacture,
                registration: this.registration,
            });

            if (["citroen", "peugeot", "ds", "vauxhall"].includes(make.toLowerCase())) {
                this.setBrand(make);
            }

            axios({
                method: "POST",
                url: "/api/osb/update-vehicle",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    bookingId: this.bookingId,
                    registration: this.registration,
                    make: make,
                    model: model,
                    fuelType: this.fuelType,
                    yearOfManufacture: this.yearOfManufacture,
                },
            })
                .then((response) => {
                    this.validator.allowInput();
                    this.collectServicePlan();
                    this.incrementStep();
                })
                .catch((error) => {
                    this.validator.errorResponse(error);
                });
        },

        handleMakeChange(event) {
            this.badgeId = badgeIds[event.target.value];

            if (this.badgeId) {
                this.getModelsForMake();
            } else {
                this.model = "";
                this.modelList = [];
            }
        },

        getModelsForMake() {
            axios({
                method: "GET",
                url: `/api/osb/models-for-make/${this.badgeId}`,
            })
                .then((response) => {
                    if (response.data.success) {
                        this.model = "";
                        return (this.modelList = response.data.models);
                    }

                    // this.setErrorMessage(response.data.message);
                    // this.setError(true);
                    this.throwFatalError();
                })
                .catch((error) => {
                    this.throwFatalError();
                    // this.setErrorMessage(error);
                    // this.setError(true);
                });
        },

        bestGuessAgeFromReg() {
            if (!this.isValidUKRegistration) return '';

            try {
                const regex = /^([A-Za-z]{2})\s{0,1}(([0-9])([0-9]))\s{0,1}([A-Za-z]{3})$/i;
                const tags = this.registration.match(regex);
                const [, , , ageId_1, ageId_2] = tags;

                return ageId_1 in yearConversion ? Number(`${yearConversion[ageId_1]}${ageId_2}`) : "";
            } catch (error) {
                console.error('Error estimating vehicle age', error);
                return '';
            }
        },
    },

    created() {
        if (!this.selectedVehicle) {
            this.setStateForNotMyVehicle();
        }

        if (this.selectedVehicle.make) {
            this.handleMakeChange({
                target: { value: this.selectedVehicle.make },
            });
        }

        this.setBrand("");
    },

    mounted() {
        this.validator = new ContactFormValidator({
            element: $(this.$refs.form),
            vueForm: true,
        });

        this.setSelectedVehicle({
            ...this.selectedVehicle,
            year_of_manufacture: this.bestGuessAgeFromReg(),
        });
    },
};
</script>

<style lang="scss" scoped>
.car-not-found {
    margin-bottom: 40px;

    &__container {
        margin-top: 37px;
    }

    &__card {
        width: 100%;
        max-width: none;
        padding: 30px;
    }

    &__sub-heading {
        font-weight: 600;
    }

    &__label {
        margin-left: 5px;
        margin-bottom: 3px;
        font-size: 12px;
    }
}

.form-row {
    width: 100%;
    min-height: 85px;
    display: flex;
    align-items: stretch;
    margin-block: 10px;
    gap: 25px;

    @media (max-width: 767px) {
        flex-direction: column;
    }

    & > div.input__container {
        width: 50%;
        max-width: 320px;
        position: relative;

        @media (max-width: 767px) {
            width: 100%;
            max-width: 100%;
        }

        & > input,
        & > select {
            display: inline-block;
            width: 100%;
            background-color: #f7f7f7;

            &::placeholder {
                color: #4a4a4a;
            }
        }

        div.validation-error {
            position: absolute;
            bottom: 0;
        }
    }
}

.form-continue {
    &__container {
        width: 100%;
        max-width: 320px;
        margin-top: 30px;

        @media (max-width: 767px) {
            max-width: 100%;
        }
    }

    &__button {
        width: 100%;
    }
}

.valid,
.active {
    border: 1px solid #0093d6;
    background-color: #ecf9ff;
    color: #0093d6;
}

.invalid {
    border: 1px solid #ff0000;
    background-color: #ffecec;
    color: #ff0000;

    &::placeholder {
        color: #ff0000;
    }
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/online-service-booking/components/CarNotFoundForm.vue